import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {AuthService} from '../../shared/services/auth.service';
import {SettingService} from '../../shared/services/setting.service';
import {SokratesSsoScriptService} from '../../shared/services/sokrates-sso-script.service';
import {UserService} from '../../shared/services/user.service';

/**
 * /login: signs in through SSO (Enforce SSO, see SokratesSsoScriptService.isEnforced), or shows the legacy login form.
 * Opened from another Sokrates app (?sso_switch=1), it always goes through SSO without the account
 * picker, and takes over that app's switch. Opened with a handed-over session (?token=&session=), it
 * signs in with that session first, as before SSO.
 */
@Component({
  selector: 'app-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.css']
})
export class AuthComponent implements OnInit {

  loading = true;
  errorMsg: string | null = null;
  showLoginForm = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private settingService: SettingService,
    private ssoScript: SokratesSsoScriptService,
    private userService: UserService,
  ) {
  }

  ngOnInit(): void {
    if (!this.settingService.getClientHostname()) {
      this.router.navigate(['/server-select']);
      return;
    }

    // Opened by another app with its session (?token=&session=, e.g. SIS): signs in with it.
    const handedOverSession = this.route.snapshot.queryParams['session'];
    if (handedOverSession) {
      this.signInWithHandedOverSession(handedOverSession);
      return;
    }

    this.continueLogin();
  }

  /** "Coba Lagi" after an error: SSO again when it is enforced, else the legacy form. */
  retry(): void {
    this.errorMsg = null;
    if (this.ssoScript.isEnforced()) {
      this.loading = true;
      this.redirectToSso({});
    } else {
      this.showLoginForm = true;
    }
  }

  /** The usual sign-in: SSO (enforced, or a switch-in from another app) or the legacy form. */
  private continueLogin(): void {
    const switchIn = this.ssoScript.takeSwitchIn();

    // Already signed in: straight to the dashboard instead of a round trip through SSO. The session
    // is checked by the API itself (SSO authorizer); a rejected request signs in again.
    if (this.authService.current() && !switchIn) {
      this.router.navigate(['/layout/dashboard']);
      return;
    }

    if (switchIn || this.ssoScript.isEnforced()) {
      this.ssoScript.setCarryFrom(switchIn?.carryFrom ?? null);
      this.redirectToSso(switchIn ? { tenantUuid: switchIn.tenantUuid, prompt: 'auto', fromOrigin: switchIn.fromOrigin } : {});
      return;
    }

    this.loading = false;
    this.showLoginForm = true;
  }

  /**
   * The token handoff from before SSO: auth-service signs in with the session the other app handed
   * over. When it cannot, the usual sign-in follows.
   */
  private signInWithHandedOverSession(sessionId: string): void {
    this.settingService.setSession(sessionId);

    this.authService.loginBySession().subscribe(
      (res: any) => {
        if (res?.error !== 0) {
          this.continueLogin();
          return;
        }

        const data = res.result.data;
        const finish = (timezone: string) => {
          this.settingService.setConfig('timezone', timezone);
          this.authService.loginSelect(data);
          this.authService.setPermissions(data, true, true);
          if (res.result.token_next?.access_token) {
            this.settingService.setTokenNext(res.result.token_next.access_token);
          }
        };

        this.userService.getTimezone(data?.token?.access_token).subscribe(
          (tzRes: any) => finish(tzRes?.error === 0 ? tzRes?.result?.data?.timezone || 'Etc/GMT-7' : 'Etc/GMT-7'),
          () => finish('Etc/GMT-7'),
        );
      },
      () => this.continueLogin(),
    );
  }

  private redirectToSso(options: { tenantUuid?: string | null; prompt?: 'auto' | null; fromOrigin?: string | null }): void {
    this.ssoScript.loginWithRedirect(options).catch(() => {
      this.loading = false;
      this.errorMsg = 'Gagal terhubung ke SSO. Silakan refresh halaman.';
    });
  }
}
